// utils/pdfExport.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper: format Name & No. segment for summary lines
const formatNameNo = (row: any): string => {
  const name = row.name ?? "";
  const number = row.number ?? "";
  if (name && number) return ` - ${name} - ${number}`;
  if (name) return ` - ${name}`;
  if (number) return ` - ${number}`;
  return "";
};

export function generateDeliveryPdf(address: any, rows: any[], order_info?: any) {
  // Filter out groups where all rows are not_included
  // Group rows by line_id
  const grouped = new Map<number, any[]>();
  for (const row of rows) {
    if (!grouped.has(row.line_id)) grouped.set(row.line_id, []);
    grouped.get(row.line_id).push(row);
  }
  // Only keep rows from groups where not all are not_included
  const filteredRows = [];
  for (const groupRows of grouped.values()) {
    if (!groupRows.every(r => r.not_included)) {
      filteredRows.push(...groupRows);
    }
  }
  rows = filteredRows;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // --- Format current date and time ---
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const formattedDate = `${day}-${month}-${year}`;
  const formattedTime = `${hours}:${minutes}`;

  // --- Calculate header height dynamically ---
  const calculateHeaderHeight = () => {
    let headerHeight = 30; // Title
    if (order_info?.order_no) {
      headerHeight += 27; // Order number space
    } else {
      headerHeight += 15; // Smaller gap if no order number
    }
    headerHeight += 15; // "Shipment address" label

    // Count address lines
    const addr = [
      address.name,
      address.address1,
      address.address2,
      `${address.postalCode} ${address.city}`,
      address.country,
    ].filter(Boolean);

    headerHeight += addr.length * 12; // Each address line
    headerHeight += 20; // Bottom padding

    return headerHeight;
  };

  const headerHeight = calculateHeaderHeight();

  // --- Group rows by line_id WHILE PRESERVING ORDER ---
  const groups = [];
  const processedLineIds = new Set();

  for (const row of rows) {
    if (!processedLineIds.has(row.line_id)) {
      processedLineIds.add(row.line_id);
      // Collect all rows with this line_id in the order they appear
      const groupRows = rows.filter(r => r.line_id === row.line_id);
      groups.push({
        line_id: row.line_id,
        rows: groupRows
      });
    }
  }

  const body: any[] = [];
  const notIncludedSummary: string[] = [];
  const outstandingSummary: string[] = [];

  // Process groups in order
  groups.forEach(({ rows: groupRows }) => {
    const first = groupRows[0];
    const qtyOrdered = first.qty_ordered || 0;
    const totalToShip = groupRows.reduce((s, r) => s + (r.qty_to_ship || 0), 0);
    const outstanding = Math.max(qtyOrdered - totalToShip, 0);

    const allNotIncluded = groupRows.every(r => r.not_included);
    const allZeroShip = groupRows.every(r => (r.qty_to_ship || 0) === 0);

    // If all are not included OR all have zero qty_to_ship → skip table, mark as not included
    if (allNotIncluded || allZeroShip) {
      notIncludedSummary.push(
        `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${qtyOrdered}`
      );
      return;
    }

    // Filter out child rows with qty_to_ship = 0 (they're considered outstanding)
    const shippedRows = groupRows.filter(r => (r.qty_to_ship || 0) > 0 && !r.not_included);

    // Skip if no shipped rows remain
    if (!shippedRows.length) {
      if (outstanding > 0) {
        outstandingSummary.push(
          `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
            first.sku_number ? `(${first.sku_number})` : ""
          } - Qty: ${outstanding}`
        );
      }
      return;
    }

    // Format box label with box_id
    const formatBoxLabel = (box: any) => {
      if (!box) return "";
      if (box.box_id) {
        return `${box.label} (${box.box_id})`;
      }
      return box.label || "";
    };

    // Add first (main) row to table
    body.push([
      first?.sku_number,
      `${first?.product_name}`,
      `${first.name ?? ""}${first?.name && first?.number ? " - " : ""}${first?.number ?? ""}`,
      first?.size,
      qtyOrdered,
      first.qty_to_ship || 0,
      outstanding,
      formatBoxLabel(first?.box),
    ]);

    // Add only shipped child rows (skip zero-qty ones)
    for (let i = 1; i < shippedRows.length; i++) {
      const r = shippedRows[i];
      body.push([
        "",
        "",
        "",
        "",
        "",
        r.qty_to_ship || "",
        "",
        formatBoxLabel(r.box),
      ]);
    }

    // Add to outstanding summary if some quantity is left
    if (outstanding > 0) {
      outstandingSummary.push(
        `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${outstanding}`
      );
    }
  });

  // --- Header drawn on each page ---
  const drawHeader = () => {
    // Left side - Title
    doc.setFontSize(16).setFont("helvetica", "bold");
    doc.text("Delivery Note", margin, 30);

    if (order_info?.order_no) {
      doc.setFontSize(10).setFont("helvetica", "bold");
      doc.text(`Order #: ${order_info.order_no}`, margin, 50);
    }

    // Right side - Date, Time
    doc.setFontSize(10).setFont("helvetica", "bold");
    const rightX = pageWidth - margin;
    doc.text(`Date: ${formattedDate}`, rightX, 30, { align: "right" });
    doc.text(`Time: ${formattedTime}`, rightX, 42, { align: "right" });

    // Shipment address
    let yPos = order_info?.order_no ? 70 : 60;
    doc.setFontSize(10).setFont("helvetica", "bold");
    doc.text("Shipment address", margin, yPos);

    const addr = [
      address.name,
      address.address1,
      address.address2,
      `${address.postalCode} ${address.city}`,
      address.country,
    ].filter(Boolean);

    doc.setFont("helvetica", "normal");
    yPos += 16;
    addr.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 12;
    });
  };

  // Draw initial header
  drawHeader();

  // --- Table with borders only between rows ---
  autoTable(doc, {
    startY: headerHeight + 20, // Start below header with padding
    head: [["SKU", "Product Name", 'Name & No.', "Size", "Qty ordered", "Qty to ship", "Outstanding", "Box"]],
    body,
    styles: {
      fontSize: 10,
      cellPadding: 3,
      halign: "center",
    },
    headStyles: {
      fillColor: [60, 60, 60],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "left" } },
    margin: {
      left: margin,
      right: margin,
      top: headerHeight + 20, // Top margin for new pages
      bottom: 60
    },
    didDrawCell: (data) => {
      // Draw border only at the top of each cell (horizontal lines between rows)
      if (data.section === 'body') {
        const { cell, cursor } = data;
        doc.setDrawColor(60, 60, 60);
        doc.setLineWidth(0.5);
        doc.line(
          cursor.x,
          cursor.y,
          cursor.x + cell.width,
          cursor.y
        );
      }
    },
    didDrawPage: (data) => {
      // Draw header on each new page
      if (data.pageNumber > 1) {
        drawHeader();
      }

      // Footer with page number (temporary, will be updated later)
      doc.setFontSize(9).setFont("helvetica", "normal");
      doc.text(`Page ${data.pageNumber}`, pageWidth - margin - 60, pageHeight - 20);
    },
  });

  let finalY = (doc as any).lastAutoTable.finalY + 20;

  // --- Render summaries with proper paging ---
  const renderList = (title: string, lines: string[]) => {
    if (!lines.length) return;

    // Check if we need a new page for the title
    if (finalY > pageHeight - 100) {
      doc.addPage();
      drawHeader();
      finalY = headerHeight + 30;
    }

    doc.setFont("helvetica", "bold").setFontSize(15);
    doc.text(title, margin, finalY);
    finalY += 16;

    doc.setFont("helvetica", "normal").setFontSize(9);
    lines.forEach(line => {
      // Check if we need a new page for content
      if (finalY > pageHeight - 60) {
        doc.addPage();
        drawHeader();
        finalY = headerHeight + 30;
      }
      doc.text(line, margin + 10, finalY);
      finalY += 12;
    });

    finalY += 20;
  };

  renderList("Items Not Included in this shipment:", notIncludedSummary);
  renderList("Outstanding Items:", outstandingSummary);

  // --- Finalize page numbers ---
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 60, pageHeight - 20);
  }

  return doc;
}


/**
 * NEW FUNCTION: Generate a PDF for a specific box
 * This creates a delivery note PDF containing only items assigned to a specific box
 */
/**
 * Generate a PDF for a specific box with enhanced product details
 */
export function generateBoxDeliveryPdf(
  address: any,
  rows: any[],
  boxLabel: string,
  boxId: string | number | null,
  order_info?: any
) {

  const allOriginalRows = [...rows];

  // Filter out groups where all rows are not_included (for table display only)
  const grouped = new Map<number, any[]>();
  for (const row of rows) {
    if (!grouped.has(row.line_id)) grouped.set(row.line_id, []);
    grouped.get(row.line_id).push(row);
  }
  const filteredRows = [];
  for (const groupRows of grouped.values()) {
    if (!groupRows.every(r => r.not_included)) {
      filteredRows.push(...groupRows);
    }
  }
  rows = filteredRows;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Format current date and time
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const formattedDate = `${day}-${month}-${year}`;
  const formattedTime = `${hours}:${minutes}`;


  // Calculate header height dynamically
  const calculateHeaderHeight = () => {
    let headerHeight = 30; // Title
    if (order_info?.order_no) {
      headerHeight += 15;
    }
    if (order_info?.po_number) {
      headerHeight += 15; // PO number line
    }
    headerHeight += 15; // Box info line
    headerHeight += 15; // Total quantity line
    headerHeight += 15; // "Shipment address" label

    const addr = [
      address?.name,
      address?.address1,
      address?.address2,
      `${address?.postalCode || ''} ${address?.city || ''}`.trim(),
      address?.country,
    ].filter(Boolean);

    headerHeight += addr.length * 12;
    headerHeight += 20;

    return headerHeight;
  };

  const headerHeight = calculateHeaderHeight();

  // Filter rows for this specific box
  const boxRows = rows.filter(r => {
    if (!r.box) return false;
    const rowBoxId = r.box.box_id || null;
    const targetBoxId = boxId || null;
    return r.box.label === boxLabel && rowBoxId === targetBoxId;
  });

  // Group all ORIGINAL rows by line_id (including not_included items)
  const allRowsByLineId = new Map<number, any[]>();
  allOriginalRows.forEach(row => {
    if (!allRowsByLineId.has(row.line_id)) allRowsByLineId.set(row.line_id, []);
    allRowsByLineId.get(row.line_id)!.push(row);
  });

  // Calculate total quantity for this box
  const totalQtyToShip = boxRows.reduce((sum, row) => {
    return sum + (row.not_included ? 0 : (row.qty_to_ship || 0));
  }, 0);

  // Group rows by line_id while preserving order
  const groups = [];
  const processedLineIds = new Set();

  for (const row of boxRows) {
    if (!processedLineIds.has(row.line_id)) {
      processedLineIds.add(row.line_id);
      const groupRows = boxRows.filter(r => r.line_id === row.line_id);
      groups.push({
        line_id: row.line_id,
        rows: groupRows
      });
    }
  }

  const body: any[] = [];
  const notIncludedSummary: string[] = [];
  const outstandingSummary: string[] = [];

  // Format box label with box_id
  const formatBoxLabel = (box: any) => {
    if (!box) return "";
    if (box.box_id) {
      return `${box.box_id}`;
    }
    return box.label || "";
  };

  // Collect ALL not-included items from ALL boxes
  const processedNotIncludedLineIds = new Set<number>();

  allRowsByLineId.forEach((lineRows, lineId) => {
    // Check if ALL rows for this line are not_included
    const allNotIncluded = lineRows.every(r => r.not_included);

    if (allNotIncluded && !processedNotIncludedLineIds.has(lineId)) {
      processedNotIncludedLineIds.add(lineId);
      const first = lineRows[0];
      const qtyOrdered = first.qty_ordered || 0;

      notIncludedSummary.push(
        `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${qtyOrdered}`
      );
    }
  });

  // Process groups in order (for table display)
  groups.forEach(({ rows: groupRows }) => {
    const first = groupRows[0];
    // Get all rows for this line_id across all boxes
    const allRowsForLine = allRowsByLineId.get(first.line_id) || [];
    const qtyOrdered = allRowsForLine[0]?.qty_ordered || 0;
    const totalShipped = allRowsForLine.reduce((sum, r) => sum + (r.not_included ? 0 : (r.qty_to_ship || 0)), 0);
    const outstanding = Math.max(qtyOrdered - totalShipped, 0);
    const totalToShip = groupRows.reduce((s, r) => s + (r.qty_to_ship || 0), 0);

    const allNotIncluded = groupRows.every(r => r.not_included);
    const allZeroShip = groupRows.every(r => (r.qty_to_ship || 0) === 0);

    // If all are not included OR all have zero qty_to_ship → skip table
    // (already added to notIncludedSummary above)
    if (allNotIncluded || allZeroShip) {
      return;
    }

    // Filter out child rows with qty_to_ship = 0 (they're considered outstanding)
    const shippedRows = groupRows.filter(r => (r.qty_to_ship || 0) > 0 && !r.not_included);

    // Skip if no shipped rows remain
    if (!shippedRows.length) {
      if (outstanding > 0) {
        outstandingSummary.push(
          `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
            first.sku_number ? `(${first.sku_number})` : ""
          } - Qty: ${outstanding}`
        );
      }
      return;
    }

    const styleInfo = first.style_name ? `Style: ${first.style_name}` : '';

    // Build product name with colors and style on separate lines
    let productDisplay = first?.product_name || '';

    if (styleInfo) {
      productDisplay += `\n${styleInfo}`;
    }

    // Add ALL shipped rows with full information
    for (let i = 0; i < shippedRows.length; i++) {
      const r = shippedRows[i];

      body.push([
        first?.sku_number,
        productDisplay,
        `${first.name ?? ""}${first?.name && first?.number ? " - " : ""}${first?.number ?? ""}`,
        first?.size,
        i === 0 ? qtyOrdered : "",
        r.qty_to_ship || 0,
        i === 0 ? outstanding : "",
        formatBoxLabel(r?.box),
      ]);
    }

    // Add to outstanding summary if some quantity is left
    if (outstanding > 0) {
      outstandingSummary.push(
        `• ${first.product_name}${formatNameNo(first)}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${outstanding}`
      );
    }
  });

  // Header drawn on each page
  const drawHeader = () => {
    let currentY = 30;

    // Left side - Title
    doc.setFontSize(16).setFont("helvetica", "bold");
    doc.text("Delivery Note", margin, currentY);

    // Right side - Date, Time
    doc.setFontSize(10).setFont("helvetica", "bold");
    const rightX = pageWidth - margin;
    doc.text(`Date: ${formattedDate}`, rightX, 30, { align: "right" });
    doc.text(`Time: ${formattedTime}`, rightX, 42, { align: "right" });

    currentY = 50;

    // Order number
    if (order_info?.order_no) {
      doc.setFontSize(10).setFont("helvetica", "bold");
      doc.text(`Order #: ${order_info.order_no}`, margin, currentY);
      currentY += 15;
    }

    // PO number
    if (order_info?.po_number) {
      doc.setFontSize(10).setFont("helvetica", "bold");
      doc.text(`PO #: ${order_info.po_number}`, margin, currentY);
      currentY += 15;
    }

    // Box info
    doc.setFontSize(10).setFont("helvetica", "bold");
    const boxDisplayText = boxId ? `${boxLabel} (${boxId})` : boxLabel;
    //doc.text(`Box: ${boxDisplayText}`, margin, currentY);
    currentY += 15;

    // Total quantity for this box
    doc.setFontSize(10).setFont("helvetica", "bold");
    doc.text(`Total Quantity: ${totalQtyToShip}`, margin, currentY);
    currentY += 15;

    // Shipment address
    doc.setFontSize(10).setFont("helvetica", "bold");
    doc.text("Shipment address", margin, currentY);

    const addr = [
      address?.name,
      address?.address1,
      address?.address2,
      `${address?.postalCode || ''} ${address?.city || ''}`.trim(),
      address?.country,
    ].filter(Boolean);

    doc.setFont("helvetica", "normal");
    currentY += 16;
    addr.forEach((line) => {
      doc.text(line, margin, currentY);
      currentY += 12;
    });
  };

  // Draw initial header
  drawHeader();

  // --- Generate box summary by product-style ---
  const summaryMap = new Map<string, number>();

  groups.forEach(({ rows: groupRows }) => {
    const first = groupRows[0];
    const allNotIncluded = groupRows.every(r => r.not_included);
    const allZeroShip = groupRows.every(r => (r.qty_to_ship || 0) === 0);

    if (allNotIncluded || allZeroShip) return;

    const shippedRows = groupRows.filter(r => (r.qty_to_ship || 0) > 0 && !r.not_included);
    if (!shippedRows.length) return;

    const totalToShip = groupRows.reduce((s, r) => s + (r.qty_to_ship || 0), 0);
    const productName = first?.product_name || '';
    const styleName = first?.style_name || '';
    const key = styleName ? `${productName} - ${styleName}` : productName;

    summaryMap.set(key, (summaryMap.get(key) || 0) + totalToShip);
  });

  // Draw box summary with highlight
  let summaryY = headerHeight + 20;
  const boxDisplayText = boxId ? `${boxId}` : boxLabel;

  if (summaryMap.size > 0) {
    doc.setFontSize(10).setFont("helvetica", "bold");

    summaryMap.forEach((qty, productStyle) => {
      const summaryText = `${boxDisplayText} has ${qty}-Pcs ${productStyle}`;
      const textWidth = doc.getTextWidth(summaryText);
      const padding = 4;

      // Draw yellow highlight background
      doc.setFillColor(255, 255, 0);
      doc.rect(margin - padding, summaryY - 10, textWidth + (padding * 2), 14, 'F');

      // Draw text
      doc.setTextColor(0, 0, 0);
      doc.text(summaryText, margin, summaryY);

      summaryY += 16;
    });

    summaryY += 10; // Extra spacing before table
  }

  // Table with box column and multi-line product names
  autoTable(doc, {
    startY: summaryY,
    head: [["SKU", "Product Name\nStyle", 'Name & No.', "Size", "Qty\nordered", "Qty to\nship", "Outstanding", "Box"]],
    body,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      valign: "top",
    },
    headStyles: {
      fillColor: [60, 60, 60],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8,
      valign: "middle",
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      0: { halign: "left", cellWidth: 45 },
      1: { halign: "left", cellWidth: 140 },
      2: { cellWidth: 60 },
      3: { cellWidth: 45 },
      4: { cellWidth: 40 },
      5: { cellWidth: 40 },
      6: { cellWidth: 55 },
      7: { cellWidth: 85 }
    },
    margin: {
      left: margin,
      right: margin,
      top: headerHeight + 20,
      bottom: 60
    },
    didDrawCell: (data) => {
      if (data.section === 'body') {
        const { cell, cursor } = data;
        doc.setDrawColor(60, 60, 60);
        doc.setLineWidth(0.5);
        doc.line(
          cursor.x,
          cursor.y,
          cursor.x + cell.width,
          cursor.y
        );
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawHeader();
      }
      doc.setFontSize(9).setFont("helvetica", "normal");
      doc.text(`Page ${data.pageNumber}`, pageWidth - margin - 60, pageHeight - 20);
    },
  });

  let finalY = (doc as any).lastAutoTable.finalY + 20;

  // --- Render summaries with proper paging ---
  const renderList = (title: string, lines: string[]) => {
    if (!lines.length) return;

    // Check if we need a new page for the title
    if (finalY > pageHeight - 100) {
      doc.addPage();
      drawHeader();
      finalY = headerHeight + 30;
    }

    doc.setFont("helvetica", "bold").setFontSize(15);
    doc.text(title, margin, finalY);
    finalY += 16;

    doc.setFont("helvetica", "normal").setFontSize(9);
    lines.forEach(line => {
      // Check if we need a new page for content
      if (finalY > pageHeight - 60) {
        doc.addPage();
        drawHeader();
        finalY = headerHeight + 30;
      }
      doc.text(line, margin + 10, finalY);
      finalY += 12;
    });

    finalY += 20;
  };

  renderList("Items Not Included in this shipment:", notIncludedSummary);
  renderList("Outstanding Items:", outstandingSummary);

  // Finalize page numbers
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9).setFont("helvetica", "normal");
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 60, pageHeight - 20);
  }

  return doc;
}

export function generateBoxSeparatedPdfs(
  address: any,
  rows: any[],
  order_info?: any
): Array<{ boxLabel: string; boxId: string | number | null; doc: jsPDF }> {
  // Keep original rows with not_included items for passing to generateBoxDeliveryPdf
  const originalRows = rows;

  // Filter out groups where all rows are not_included (only for finding boxes)
  const grouped = new Map<number, any[]>();
  for (const row of rows) {
    if (!grouped.has(row.line_id)) grouped.set(row.line_id, []);
    grouped.get(row.line_id).push(row);
  }
  const filteredRows = [];
  for (const groupRows of grouped.values()) {
    if (!groupRows.every(r => r.not_included)) {
      filteredRows.push(...groupRows);
    }
  }

  // Get unique boxes from filtered rows (boxes that actually have items to ship)
  const boxMap = new Map<string, { label: string; id: string | number | null }>();

  filteredRows.forEach(row => {
    if (row.box && row.box.label) {
      const normalizedId = row.box.box_id || null;
      const key = `${row.box.label}-${normalizedId ?? 'null'}`;
      if (!boxMap.has(key)) {
        boxMap.set(key, {
          label: row.box.label,
          id: normalizedId
        });
      }
    }
  });

  // Generate a PDF for each box
  const pdfs: Array<{ boxLabel: string; boxId: string | number | null; doc: jsPDF }> = [];

  boxMap.forEach(({ label, id }) => {
    const doc = generateBoxDeliveryPdf(address, originalRows, label, id, order_info);
    pdfs.push({
      boxLabel: label,
      boxId: id,
      doc
    });
  });

  return pdfs;
}

// Attach Ctrl+P shortcut
export function enableDeliveryPdfShortcut(address: any, rows: any[], order_info?: any): void {
    const handler = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
            e.preventDefault();
            const docs = generateBoxSeparatedPdfs(address, rows, order_info);
            docs.forEach(docObj => {
                docObj.doc.autoPrint();
                window.open(docObj.doc.output("bloburl"), "_blank");
            });
        }
    };

    window.removeEventListener("keydown", handler); // ensure only one
    window.addEventListener("keydown", handler);
}

/**
 * Sorts an array of rows by box info, then factory_product_id, then line_id.
 * @param rows - Array of rows to sort
 * @param assignmentOrder - Optional Map of box identifier to order
 * @returns Sorted array of rows
 */
export function sortRowsByBox(
  rows: any[],
  boxAssignmentOrder?: Map<string, number>
): any[] {
  // Return a new sorted array (do not mutate original)
  return [...rows].sort((a, b) => {
    // Helper: combined box identifier
    const combined = (row: any) => {
      const label = row.box?.label || '';
      const id = row.box?.box_id || '';
      return label && id ? `${label} (${id})` : '';
    };

    const combinedA = combined(a);
    const combinedB = combined(b);

    // Sort by combined box identifier first
    if (combinedA !== combinedB) {
      if (!combinedA) return 1; // empty boxes last
      if (!combinedB) return -1;

      if (boxAssignmentOrder) {
        const orderA = boxAssignmentOrder.get(combinedA) ?? Infinity;
        const orderB = boxAssignmentOrder.get(combinedB) ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
      }

      // Fallback string comparison
      return combinedA.localeCompare(combinedB);
    }

    // Sort by factory_product_id
    if (a.factory_product_id !== b.factory_product_id) {
      return String(a.factory_product_id).localeCompare(String(b.factory_product_id));
    }

    // Sort by line_id as last resort
    return a.line_id - b.line_id;
  });
}
